/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Step 2 study-level service — loads sites + workflows for study level,
 * loads users per site, lazy-loads user details, and postprocesses user
 * selection state based on RCM group membership and existing tasks.
 *
 * Migrated from: app/view/app/eos/set-up/step2/eosSetUpStep2.study.service.js
 */

import { eosApiService } from '../api/api.eos'
import { getUuid, getIsMccEnabled, enrichUsers, usersPostprocess, usersSort } from './eos.service'
import { EOS_WORKFLOW_STATUS, EOS_WORKFLOW_STEP_TYPE, EOS_TASK_STATUS } from '../../constants/eos'
import type { EosConfig, EosOptions, EosSite, EosSitesMap, EosUser, EosWorkflow } from '../../types/eos'

// ---------------------------------------------------------------------------
// Types for internal API responses
// ---------------------------------------------------------------------------

interface SitesApiResponse {
  elements: Array<{
    uuid: string
    name: string
    sanitizedName?: string
    rcmFolderId?: number
    [key: string]: unknown
  }>
}

interface UsersApiResponse {
  elements: EosUser[]
}

// ---------------------------------------------------------------------------
// loadStudyLevelData — main data loader for study-level Step 2
// ---------------------------------------------------------------------------

interface StudyLevelDataResult {
  sitesMap: EosSitesMap
  activeWfs: EosWorkflow[] | null
}

/**
 * Loads workflows and sites for the selected study folder.
 * Builds the sitesMap with a special 'study' entry and maps active/finished
 * workflows to each site.
 */
export async function loadStudyLevelData(config: EosConfig, _options: EosOptions): Promise<StudyLevelDataResult> {
  const studyUuid = getIsMccEnabled(config) && config.environment ? config.environment!.uuid : config.study!.uuid

  const [wfsResp, sitesResp] = await Promise.all([
    eosApiService.getFolderWfs(config.folder!.id, studyUuid),
    eosApiService.getSitesForStudy(getUuid(config), getIsMccEnabled(config), config.folder!.id)
  ])

  const wfs = wfsResp as EosWorkflow[]
  const sitesRaw = (sitesResp as SitesApiResponse).elements ?? []

  // Sort sites by name and add metadata
  const sites = sitesRaw
    .slice()
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
    .map((site) => ({
      ...site,
      isSite: true,
      groupName: `${config.folder!.id}-${site.sanitizedName}`
    }))

  const activeWfs = wfs && wfs.length > 0 ? wfs : null

  // Build sitesMap with 'study' entry as the base
  const sitesMap: EosSitesMap = {
    study: {
      isStudy: true,
      uuid: 'study',
      name: config.study!.name,
      groupName: `${config.folder!.id}-${config.folder!.name}`,
      sanitizedName: config.folder!.name,
      activeWf: findWf(wfs, config.folder!.id, EOS_WORKFLOW_STATUS.ACTIVE),
      finishedWf: findWf(wfs, config.folder!.id, EOS_WORKFLOW_STATUS.FINISHED),
      inProgress: config.folder!.studyInfo
        ? ((config.folder!.studyInfo as { inProgress?: boolean }).inProgress ?? false)
        : false
    } as EosSite
  }

  // Add each site to the map with its workflow references
  for (const site of sites) {
    sitesMap[site.uuid] = {
      ...site,
      activeWf: findWf(wfs, site.rcmFolderId, EOS_WORKFLOW_STATUS.ACTIVE),
      finishedWf: findWf(wfs, site.rcmFolderId, EOS_WORKFLOW_STATUS.FINISHED)
    } as EosSite
  }

  return { sitesMap, activeWfs }
}

// ---------------------------------------------------------------------------
// reloadUsers / loadUsers — load users for a specific site
// ---------------------------------------------------------------------------

/**
 * Reloads users for a site, preserving selections if users were previously loaded.
 */
export async function reloadUsers(site: EosSite, config: EosConfig): Promise<void> {
  await loadUsers(site, config, !!site.users)
}

/**
 * Loads users for a site and postprocesses them.
 * @param keepSelections - if true, preserves existing isSelected/wasSelected from previous load
 */
export async function loadUsers(site: EosSite, config: EosConfig, keepSelections: boolean = false): Promise<void> {
  const req = fetchUsers(site, config)

  Object.assign(site, {
    isLoading: true,
    usersRequest: req
  })

  const users = await req
  postprocessUsers(users, config, site, keepSelections)
}

// ---------------------------------------------------------------------------
// loadUserDetails — batch lazy-load user details
// ---------------------------------------------------------------------------

/**
 * Loads detailed user data (tasks, groups, etc.) for a batch of users.
 * Called lazily when a site row is expanded in the table.
 *
 * Mutates users in-place with the extended data.
 */
export async function loadUserDetails(users: EosUser[], config: EosConfig): Promise<void> {
  if (users.length === 0) {
    return
  }

  try {
    const resp = (await eosApiService.getUserDetails(
      {
        forStudyAndAllSites: false,
        folderId: config.folder!.id,
        mccEnabled: getIsMccEnabled(config),
        studyName: config.study!.name,
        studyUuid: config.study!.uuid,
        envUuid: config.environment?.uuid ?? undefined
      },
      users as unknown as Parameters<typeof eosApiService.getUserDetails>[1]
    )) as { elements: EosUser[] }

    const data = resp.elements ?? resp

    users.forEach((user, index) => {
      Object.assign(user, { wasLoaded: true }, data[index])

      postprocessSingleUser(user, config, user.siteRef!, false)

      if (user.postprocess) {
        user.postprocess()
        delete user.postprocess
      }
    })
  } catch {
    // Silently fail (matches legacy behavior)
  }
}

// ---------------------------------------------------------------------------
// Internal: fetchUsers — routes to the correct API based on site type
// ---------------------------------------------------------------------------

async function fetchUsers(site: EosSite, config: EosConfig): Promise<EosUser[]> {
  if (site.isStudy) {
    return fetchStudyOwners(config)
  } else {
    return fetchSiteUsers(site, config)
  }
}

async function fetchStudyOwners(config: EosConfig): Promise<EosUser[]> {
  const studyUuid = config.study!.uuid
  const folderId = config.folder!.id
  const mccEnabled = getIsMccEnabled(config)

  const resp = (await eosApiService.getUsersForStudyLight(studyUuid, {
    folderId,
    studyName: config.study!.name,
    mccEnabled,
    envUuid: mccEnabled && config.environment ? config.environment.uuid : undefined
  })) as UsersApiResponse

  return enrichUsers(resp.elements ?? [])
}

async function fetchSiteUsers(site: EosSite, config: EosConfig): Promise<EosUser[]> {
  const folderId = config.folder!.id
  const mccEnabled = getIsMccEnabled(config)

  const resp = (await eosApiService.getUsersForSite(site.uuid, {
    folderId,
    siteName: site.name,
    mccEnabled,
    envUuid: mccEnabled && config.environment ? config.environment.uuid : undefined,
    siteFolderId: site.rcmFolderId ?? 'null'
  })) as UsersApiResponse

  const users = resp.elements ?? []
  usersPostprocess(users)
  return usersSort(users)
}

// ---------------------------------------------------------------------------
// Internal: postprocessing — set selection state based on group membership
// ---------------------------------------------------------------------------

function postprocessUsers(users: EosUser[], config: EosConfig, site: EosSite, keepSelections: boolean): void {
  // Preserve existing selections when reloading
  if (keepSelections && users && site.users) {
    for (const oldUser of site.users) {
      const newUser = users.find((u) => u.uuid === (oldUser as EosUser).uuid)
      if (newUser) {
        Object.assign(newUser, {
          isSelected: (oldUser as EosUser).isSelected,
          wasSelected: (oldUser as EosUser).wasSelected
        })
      }
    }
  }

  Object.assign(site, {
    isLoading: false,
    usersLoaded: true,
    users,
    displayUsers: users
  })

  for (const user of users) {
    postprocessSingleUser(user, config, site, keepSelections)
  }
}

/**
 * Postprocesses a single user — determines selection state based on RCM group
 * membership, and resolves download/acknowledge tasks for the user's site.
 *
 * This is the core logic that maps raw API data into the checkbox state
 * displayed in the table.
 */
export function postprocessSingleUser(
  user: EosUser,
  config: EosConfig,
  site: EosSite,
  keepSelections: boolean = false
): void {
  const isSelected = keepSelections
    ? !!user.isSelected
    : !!(user.alreadyPresentInRcmGroups && user.alreadyPresentInRcmGroups.includes(site.groupName!)) ||
      !!(user.alreadyPresentInRcmGroups && user.alreadyPresentInRcmGroups.includes((site?.groupName ?? '') + '-Full'))

  const wasSelected = keepSelections ? !!user.wasSelected : isSelected

  // Find tasks for this specific site/study folder
  const eosFolderId = site.isStudy ? config.folder!.id : site.rcmFolderId!

  const siteTasks = (user.tasks ?? []).filter((task) => task.eosFolderId === eosFolderId)

  const validStatuses: (string | null)[] = [
    EOS_TASK_STATUS.APPROVED,
    EOS_TASK_STATUS.OPEN,
    EOS_TASK_STATUS.CANCELLED,
    EOS_TASK_STATUS.INACTIVE,
    null
  ]

  const taskDownload =
    siteTasks.find(
      (task) => task.type === EOS_WORKFLOW_STEP_TYPE.DOWNLOAD_EOS && validStatuses.includes(task.status)
    ) ?? null

  const taskAcknowledge =
    siteTasks.find(
      (task) => task.type === EOS_WORKFLOW_STEP_TYPE.ACKNOWLEDGE_EOS && validStatuses.includes(task.status)
    ) ?? null

  const isAcknowledgeSelected = !!taskAcknowledge
  const wasDownloadSelected = !!taskDownload

  Object.assign(user, {
    belongsTo: site.uuid,
    wasLoaded: !site.isStudy || user.wasLoaded,
    isSelected,
    wasSelected,
    taskAcknowledge,
    taskDownload,
    isAcknowledgeSelected,
    wasAcknowledgeSelected: isAcknowledgeSelected,
    isDownloadSelected: wasDownloadSelected,
    wasDownloadSelected
  })
}

// ---------------------------------------------------------------------------
// Internal helper: find workflow by folderId and status
// ---------------------------------------------------------------------------

function findWf(wfs: EosWorkflow[], folderId: number | undefined, status: string): EosWorkflow | null {
  if (!wfs || !folderId) return null
  return wfs.find((wf) => wf.folderId === folderId && wf.status === status) ?? null
}
